/**
 * Remove every `undefined` from a value on its way into Firestore.
 *
 * Firestore refuses `undefined` outright, and the message it gives back names
 * the document but not the field: *"Unsupported field value: undefined (found
 * in document skidaCrmShareholders/…)"*. So the cost of one stray key is a
 * whole record that cannot be saved and a person staring at a red box with no
 * idea which of forty fields did it. This has happened twice — an untouched
 * issuance carrying `paymentRequestSentAt: undefined`, and a share purchase
 * with no note once `note` became a field.
 *
 * ## Deep, because the records are
 *
 * The first version of this ran one level deep, which held exactly as long as
 * every field was a scalar. A shareholder is not: purchases, options,
 * subscriptions and attachments are arrays of objects, and an `undefined`
 * inside one is just as fatal and completely invisible to a top-level filter.
 *
 * ## What it does not touch
 *
 * Arrays keep their length — an element is cleaned, never dropped. A purchase
 * quietly disappearing from a cap table is far worse than a write that fails.
 *
 * Anything that is not a plain object is handed over as it is: `Date`,
 * Firestore's `Timestamp`, and the `FieldValue` sentinels behind
 * `serverTimestamp()` are objects whose internals must survive intact.
 *
 * `null` is left alone. It is a value somebody meant — an unset price is
 * `null`, and turning that into an absent key would let a stale one survive
 * underneath a merge.
 */
export function strip<T>(value: T): T {
  if (Array.isArray(value)) return value.map(strip) as unknown as T
  if (value === null || typeof value !== 'object') return value
  if ((value as object).constructor !== Object) return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, strip(v)]),
  ) as T
}
